import { PropsWithChildren, useEffect, useMemo, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useSsoUserGroupAccess } from '#features';
import {
  Loader,
  LOCAL_STORAGE_KEYS,
  resolveNetworkRedirect,
  routes,
  useNetworkState,
} from '#shared';

import type { SsoUserGroup } from '#shared';

interface RouteGuardProps extends PropsWithChildren {
  ssoUserGroups?: SsoUserGroup[];
}

export const RouteGuard = ({
  children,
  ssoUserGroups = [],
}: RouteGuardProps) => {
  const { defaultPage, isDefaultPage, currentAccess, grantedGroups, status } =
    useSsoUserGroupAccess(ssoUserGroups);
  const targetPath = localStorage.getItem(LOCAL_STORAGE_KEYS.SSO);
  const networkState = useNetworkState();

  const { pathname } = useLocation();

  // Static kiosks must not be moved by mid-session network changes — the
  // redirect decision is taken only on initial readiness and on subsequent
  // user-driven path changes. The latest network snapshot is read via a ref
  // so a fresh recheck (e.g. triggered by a nav-menu click) does not
  // re-trigger the redirect memo and yank the user off the current page.
  const networkRef = useRef(networkState);
  networkRef.current = networkState;

  const permissionRedirect = useMemo(() => {
    if (ssoUserGroups.length) {
      if (targetPath && pathname !== targetPath) {
        return targetPath;
      }

      if (currentAccess && isDefaultPage && pathname !== defaultPage) {
        return defaultPage;
      }

      if (currentAccess && pathname === routes.NO_ACCESS && defaultPage) {
        return defaultPage;
      }

      if (!currentAccess && defaultPage) {
        return defaultPage;
      }

      if (!currentAccess && !defaultPage && pathname !== routes.NO_ACCESS) {
        return routes.NO_ACCESS;
      }
    }
  }, [
    currentAccess,
    defaultPage,
    isDefaultPage,
    pathname,
    ssoUserGroups.length,
    targetPath,
  ]);

  const isReady = status !== 'pending' && !networkState.isChecking;

  const redirectRoute = useMemo(() => {
    if (!isReady) {
      return undefined;
    }
    // Network-driven fallback (private down → push to Stats, public down →
    // push to Main app, etc.) only applies at the bare entry point. Once the
    // user is on a specific URL — typed, bookmarked, PWA-restored, or
    // reloaded — we trust their intent and let them stay. The page renders
    // with whatever data it can; the nav menu remains available to switch
    // sides manually if they want to. Permission redirects still apply on
    // every route.
    if (pathname !== routes.START_PAGE) {
      return permissionRedirect;
    }
    // Read latest network state via ref so mid-session re-checks do NOT
    // recompute this memo — the decision re-runs only on path / perm changes
    // and on initial readiness.
    return resolveNetworkRedirect(
      permissionRedirect,
      pathname,
      networkRef.current,
      grantedGroups,
    );
  }, [permissionRedirect, pathname, grantedGroups, isReady]);

  useEffect(() => {
    if (status !== 'pending') {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.SSO);
    }
  }, [status]);

  return (
    <>
      {isReady ? (
        (redirectRoute && <Navigate to={redirectRoute} />) || children
      ) : (
        <Loader />
      )}
    </>
  );
};
